import { Icon, Menu, MenuItem } from "@material-ui/core";
import {
    BugReport as BugReportIcon,
    Edit as EditIcon,
    LiveHelpOutlined as LiveHelpIcon,
    MenuBookOutlined as MenuBookIcon,
    AddCircleOutlineOutlined as AddIcon,
    ArrowDropUp,
    ArrowDropDown
} from "@material-ui/icons";
import React, { Component } from "react";
import styled from "styled-components";

import { WorkspacePlanTier, WorkspaceUserRole } from "common/interfaces";
import { Divider } from "js/Components/Divider";
import { buildVersion, isInternalBAIUser } from "js/config";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { openTeamUpgrade } from "js/core/utilities/externalLinks";
import { isShift } from "js/core/utilities/keys";
import { trackActivity } from "js/core/utilities/utilities";
import { app } from "js/namespaces";
import Avatar from "js/react/components/Avatar";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import FeedbackDialog from "js/react/components/Dialogs/FeedbackDialog";
import { Gap15, Gap20, Gap5 } from "js/react/components/Gap";
import SearchInput from "js/react/components/SearchInput";
import { themeColors } from "js/react/sharedStyles";
import ShortcutsDialog from "js/react/views/PPTAddin/Components/ShortcutsDialog";

import { PanelType } from "../PresentationEditor/PresentationEditorController";
import { generateTeamWorkspaceAvatar } from "js/core/utilities/avatarGenerator";
import { getWorkspaceUsers } from "js/controllers/WorkspaceController";
import GuestBadge from "js/react/components/GuestBadge";
import { withTranslation } from "react-i18next";

const logger = getLogger(LogGroup.HELP_MENU);

const Container = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    .bai-popup {
        .bai-icon {
            color: white !important;
            font-size: 26px;
        }
        .MuiSvgIcon-root {
            color: #666;
            margin-right: 6px;
            font-size: 1.5rem;
        }
    }
`;

const StyledMenu = styled(Menu)`
    &&& {
        width: auto;
        .MuiPaper-root {
            max-height: 80vh;
            display: flex;
            flex-direction: column;
        }
        .MuiList-root {
            display: flex;
            flex-direction: column;
            max-height: inherit;
            overflow: hidden;
            width: 100%;
        }
        .MuiMenuItem-root {
            width: 100%;
            box-sizing: border-box;
        }
    }
`;

const InfoBlock = styled.div`
    padding: 15px;
    color: rgba(0, 0, 0, 0.4);
    font-size: 10px;
    pointer-events: auto;
    user-select: text;
    max-width: 220px;
`;

const Info = styled.div`
    padding: 15px;
    color: rgba(0, 0, 0, 0.4);
    font-size: 10px;
    pointer-events: auto;
    user-select: text;
    max-width: 220px;
`;

const BuildVersion = styled.span`
    word-wrap: break-word;
    word-break: break-all;
    white-space: normal;
`;

const WorkspaceTitle = styled.div`
    color: #999;
    font-family: "Source Sans Pro";
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`;

const SelectedWorkspace = styled.span`
    color: ${({ isSelected }) => isSelected ? themeColors.ui_blue : "inherit"};
`;

const WorkspaceNameContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

const WorkspaceNameText = styled.span`
    color: #FFF;
    font-size: 14px;
    font-weight: 600;
    line-height: 150%;
`;

const UserMenuTrigger = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    cursor: pointer;
`;

const RightEdgeGuard = styled.div`
    display: flex;
    align-items: center;
`;

const WorkspaceMenuItem = styled(MenuItem)`
    &&& {
        display: flex;
        align-items: center;
        justify-content: space-between;
        
        >div {
            display: flex;
            align-items: center;
            justify-content: flex-start;
        }

        &:hover {
            color: white !important;

            *:not(${RightEdgeGuard}):not(${RightEdgeGuard} *) {
                color: white !important;
            }
        }
    }
`;

const SearchContainer = styled.div`
    padding: 5px 15px;
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const MenuSearchInput = styled(SearchInput)`
    position: relative;
    display: flex;

    &.searchbar {
        align-items: center;
        padding: 8px 10px;
        border: 1px solid #EEE;
    }

    & > .micon:first-child {
        font-size: 16px;
        margin-right: 10px;
        margin-left: 5px;
        color: #11a9e2;
    }

    input[type="text"] {
        color: #333;
        width: 100%;
        font-size: 14px;
        font-weight: normal;
        border: none;
        outline: none;
        background: none;
        padding-right: 32px;

        &::placeholder {
            font-weight: 500;
        }
    }

    .clear_search {
        position: absolute;
        right: 8px;
        color: #999;
        font-size: 16px;
        cursor: pointer;
    }
`;

const NoResultsMessage = styled.div`
    padding: 15px 20px;
    text-align: center;
    color: #666;
    font-size: 12px;
    font-style: italic;
`;

const ScrollableMenuContent = styled.div`
    flex: 1;
    overflow-y: auto;
    min-height: 0;
`;

const StickyMenuFooter = styled.div`
    flex-shrink: 0;
    border-top: 1px solid #e0e0e0;
    background: white;
`;

function isTeamOrEnterprise(workspaceMetadata) {
    return [WorkspacePlanTier.TEAM, WorkspacePlanTier.ENTERPRISE].includes(workspaceMetadata.plan.tier);
}

class UserMenuBase extends Component {
    state = {
        anchorEl: null,
        shiftIsPressed: false,
        workspaceSearchQuery: "",
        generatedAvatarComponents: {},
        workspacesMenuWidth: null
    }

    focusWorkspaceItem = direction => {
        const container = document.getElementById("user-menu-workspace-list");
        if (!container) return;
        const items = Array.from(container.querySelectorAll(".workspace-menu-item.MuiMenuItem-root:not([disabled])"));
        if (!items || items.length === 0) return;
        if (direction === "first") items[0].focus();
        else if (direction === "last") items[items.length - 1].focus();
    }

    handleSearchKeyDown = e => {
        const key = e.key;

        e.stopPropagation();

        if (key === "ArrowDown" || key === "ArrowUp") {
            e.preventDefault();
            if (key === "ArrowUp") this.focusWorkspaceItem("last");
            else this.focusWorkspaceItem("first");
        }
    }

    handleWorkspaceListKeyDownCapture = e => {
        const key = e.key;
        if (key !== "ArrowDown" && key !== "ArrowUp") return;
        // prevent MUI/Menu default focus handling
        e.preventDefault();
        e.stopPropagation();
        const container = document.getElementById("user-menu-workspace-list");
        if (!container) return;
        const items = Array.from(container.querySelectorAll(".workspace-menu-item.MuiMenuItem-root:not([disabled])"));
        if (!items || items.length === 0) return;
        const active = document.activeElement;
        const index = items.indexOf(active);
        let nextIndex = index;
        if (key === "ArrowDown") nextIndex = index < items.length - 1 ? index + 1 : 0;
        if (key === "ArrowUp") nextIndex = index > 0 ? index - 1 : items.length - 1;
        items[nextIndex].focus();
    }

    get user() {
        return AppController.user || app.user;
    }

    async componentDidMount() {
        document.addEventListener("keydown", this.handleKeyDown);

        // Generate avatar components for current workspace if needed
        this.ensureHasWorkspaceAvatarComponents();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.anchorEl !== this.state.anchorEl && this.state.anchorEl) {
            const workspaceMenu = document.getElementsByClassName("MuiPaper-root MuiMenu-paper MuiPopover-paper MuiPaper-elevation8 MuiPaper-rounded");
            setTimeout(() => {
                this.setState({ workspacesMenuWidth: workspaceMenu[0].clientWidth });
            }, 0);
        }
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown);
        // No cleanup needed for React components
    }

    handleKeyDown = event => {
        if (event.key === "Escape") {
            this.closeMenu();
        }
        if (isShift(event.which)) {
            this.setState({ shiftIsPressed: true });
        }
    }

    showMenu = event => {
        this.setState({
            anchorEl: event.currentTarget
        });
    }

    showHelp = event => {
        if (!this.state.helpAnchorEl) {
            trackActivity("HelpMenu", "Open", null, null, {}, { audit: false });
        }
        this.setState({
            helpAnchorEl: event.currentTarget
        });
    }

    closeMenu = event => {
        this.setState({
            anchorEl: null,
            helpAnchorEl: null,
            workspaceSearchQuery: ""
        });
    }

    handleEditProfile = () => {
        AppController.showAccountPane({ pane: "me" });
    }

    handleSwitchWorkspace = workspaceId => {
        this.closeMenu();

        if (window.roomID && window.roomWorkspaceSwitched) {
            window.roomWorkspaceSwitched(workspaceId);
            return;
        }

        AppController.switchWorkspace(workspaceId);
    }

    handleOpenZendesk = () => {
        trackActivity("HelpMenu", "ChatSupport", null, null, {}, { audit: false });
        if (window.zendeskInit) {
            // Ensure zendeskInit returns a promise or the widget is loaded before opening messenger
            const openZendeskWidget = () => {
                if (window.zE) {
                    window.zE(() => {
                        try {
                            window.zE("messenger:open"); // Zendesk Messenger (modern)
                        } catch (e) {
                            try {
                                window.zE("webWidget", "open"); // Zendesk Classic Web Widget fallback
                            } catch (err) {
                                // Optionally log or handle error
                            }
                        }
                    });
                }
            };
            const result = window.zendeskInit();
            if (result && typeof result.then === "function") {
                result.then(openZendeskWidget);
            } else {
                setTimeout(openZendeskWidget, 250); // fallback: slight delay to allow widget to load
            }
        }
        this.closeMenu();
    }

    handleLinkToHelpCenter = () => {
        trackActivity("HelpMenu", "HelpCenter", null, null, {}, { audit: false });
        window.open("https://support.beautiful.ai", "_blank");
        this.closeMenu();
    }

    handleShowFeedbackDialog = () => {
        trackActivity("HelpMenu", "GiveFeedback", null, null, {}, { audit: false });
        ShowDialog(FeedbackDialog);
        this.closeMenu();
    }

    handleShowShortcutsDialog = () => {
        ShowDialog(ShortcutsDialog);
        this.closeMenu();
    }

    handleLogout = () => {
        if (window.roomID && window.roomUserLoggedOut) {
            window.roomUserLoggedOut();
            return;
        }

        trackActivity("Auth", "logout", null, null, { workspace_id: "all" }, { audit: true });
        ds.shutdown();
        window.location = "/logout";
    }

    handleCreateWorkspace = () => {
        openTeamUpgrade();
    };

    renderSwitchWorkspaceMenu() {
        const { canSwitchWorkspace, workspaceId } = this.props;
        const { workspaceSearchQuery } = this.state;

        if (!this.user || !this.user.workspacesMetadata) {
            return null;
        }

        const workspaces = this.user.workspacesMetadata.filter(m => m.userRole !== WorkspaceUserRole.GUEST).sort((a, b) => a.plan.tier.localeCompare(b.plan.tier));
        const workspacesByTier = Object.values(WorkspacePlanTier).reduce((acc, tier) => {
            const workspacesForTier = workspaces.filter(workspace => workspace.plan.tier === tier);
            if (workspacesForTier.length > 0) {
                acc[tier] = workspacesForTier;
            }
            return acc;
        }, {});
        Object.values(workspacesByTier).forEach(workspaces => { // sort by createdAt
            workspaces.sort((a, b) => a.createdAt - b.createdAt);
        });
        const guestWorkspaces = this.user.workspacesMetadata.filter(m => m.userRole === WorkspaceUserRole.GUEST).sort((a, b) => a.createdAt - b.createdAt);

        const workspace = this.user.workspacesMetadata.find(workspace => workspace.id === workspaceId);

        const MenuItems = [];
        const searchQuery = (workspaceSearchQuery || "").trim().toLowerCase();

        const WorkspaceItem = ({ ws }) => {
            return (
                <div>
                    {ws === workspace && <Icon style={{
                        color: themeColors.ui_blue,
                        marginRight: "0"
                    }}>check</Icon>}
                    {ws !== workspace && this.renderAvatar(ws, { style: { width: "24px", height: "24px" } })}
                    <Gap15 />
                    <SelectedWorkspace isSelected={ws === workspace}>{ws.name}</SelectedWorkspace>
                </div>
            );
        };

        Object.entries(workspacesByTier).forEach(([tier, workspaces]) => {
            workspaces.forEach(ws => {
                if (!canSwitchWorkspace && ws !== workspace) return;

                if (searchQuery && !ws.name.toLowerCase().includes(searchQuery)) return;
                MenuItems.push(<WorkspaceMenuItem className="workspace-menu-item"
                    key={ws.id}
                    value={ws.id}
                    disabled={!canSwitchWorkspace}
                    onClick={() => this.handleSwitchWorkspace(ws.id)}
                >
                    <WorkspaceItem ws={ws} />
                </WorkspaceMenuItem>);
            });
        });

        if (guestWorkspaces.length > 0) {
            guestWorkspaces.forEach(ws => {
                if (!canSwitchWorkspace && ws !== workspace) return;

                if (searchQuery && !ws.name.toLowerCase().includes(searchQuery)) return;
                MenuItems.push(<WorkspaceMenuItem className="workspace-menu-item"
                    key={ws.id}
                    value={ws.id}
                    disabled={!canSwitchWorkspace}
                    onClick={() => this.handleSwitchWorkspace(ws.id)}
                >
                    <WorkspaceItem ws={ws} />
                    <Gap15 />
                    <RightEdgeGuard>
                        <GuestBadge />
                    </RightEdgeGuard>
                </WorkspaceMenuItem>);
            });
        }

        if (searchQuery && MenuItems.length === 0) {
            MenuItems.push(
                <NoResultsMessage key="no-results">
                    No workspaces found
                </NoResultsMessage>
            );
        }

        return (
            <div>
                {MenuItems.map(Item => Item)}
            </div>
        );
    }

    ensureHasWorkspaceAvatarComponents = async () => {
        const { user } = this.props;

        if (!user || !user.workspacesMetadata) {
            return;
        }

        const avatarPromises = user.workspacesMetadata
            .filter(workspace => isTeamOrEnterprise(workspace) || workspace.userRole === WorkspaceUserRole.GUEST)
            .map(async workspace => {
                const avatarComponent = await this.generateWorkspaceAvatarComponent(workspace);
                return { workspaceId: workspace.id, avatarComponent };
            });

        const avatarResults = await Promise.all(avatarPromises);
        const avatarComponents = {};
        avatarResults.forEach(({ workspaceId, avatarComponent }) => {
            avatarComponents[workspaceId] = avatarComponent;
        });

        this.setState({ generatedAvatarComponents: avatarComponents });
    }

    getAvatarComponent(workspace) {
        const { generatedAvatarComponents } = this.state;
        if (isTeamOrEnterprise(workspace) || workspace.userRole === WorkspaceUserRole.GUEST) {
            return generatedAvatarComponents[workspace.id];
        }

        return null; // Will fall back to regular Avatar component with user photo
    }

    getAvatarUrl(workspace) {
        const { generatedAvatarComponents } = this.state;
        if (workspace.userRole === WorkspaceUserRole.GUEST) {
            const avatarData = generatedAvatarComponents[workspace.id];
            // For guest workspaces, avatarData is a photo URL string
            return avatarData || AppController.userAvatarUrl;
        }

        return AppController.userAvatarUrl;
    }

    renderAvatar(workspace, props = {}) {
        const AvatarComponent = this.getAvatarComponent(workspace);
        const avatarUrl = this.getAvatarUrl(workspace);

        return AvatarComponent && typeof AvatarComponent === "function"
            ? <AvatarComponent {...props} />
            : <Avatar url={avatarUrl} {...props} />;
    }

    generateWorkspaceAvatarComponent = async workspace => {
        if (workspace.userRole === WorkspaceUserRole.GUEST) {
            // For guest workspaces, we need to get the owner's photo URL
            try {
                const users = await getWorkspaceUsers(workspace.id, undefined, [WorkspaceUserRole.OWNER]);
                const owner = users.sort((a, b) => a.createdAt - b.createdAt)[0];
                return owner.photoURL; // Return the photo URL for guest workspaces
            } catch (error) {
                logger.error("Failed to get owner photo for guest workspace", { workspaceId: workspace.id, error });
                return null;
            }
        } else {
            // Generate a React component for team/enterprise workspaces
            return generateTeamWorkspaceAvatar(workspace.name, 24);
        }
    }

    render() {
        const { workspaceId, showDivider = true, showWorkspaceName = true, canSwitchWorkspace, t } = this.props;
        const { anchorEl, shiftIsPressed, helpAnchorEl, workspacesMenuWidth } = this.state;

        if (!this.user || !this.user.workspacesMetadata) {
            return null;
        }

        const workspace = this.user.workspacesMetadata.find(workspace => workspace.id === workspaceId);

        if (window.isZoomApp) {
            return (<>
                {this.renderAvatar(workspace, { id: "userMenu", onClick: this.showMenu, style: { width: "24px", height: "24px" } })}
                <Menu
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={this.closeMenu}
                >
                    {this.renderSwitchWorkspaceMenu()}
                    <hr />
                    <MenuItem id="menu-item-logout" onClick={this.handleLogout}>
                        <Icon>logout</Icon>
                        {t("Log out")}
                    </MenuItem>
                </Menu>
            </>);
        }

        return (
            <Container>
                <Icon onClick={this.showHelp} style={{ cursor: "pointer" }}>help</Icon>
                <Menu open={Boolean(helpAnchorEl)}
                    anchorEl={helpAnchorEl}
                    getContentAnchorEl={null}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right"
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    onClose={this.closeMenu}
                >
                    <MenuItem onClick={this.handleOpenZendesk}>
                        <LiveHelpIcon />
                        <span>{t("Chat with Support")}</span>
                    </MenuItem>
                    <MenuItem onClick={this.handleLinkToHelpCenter}>
                        <MenuBookIcon />
                        <span>{t("Browse Help Center")}</span>
                    </MenuItem>
                    <MenuItem onClick={this.handleShowFeedbackDialog}>
                        <EditIcon />
                        <span>{t("Give Feedback")}</span>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        trackActivity("HelpMenu", "DebugInfo", null, null, {}, { audit: false });
                        AppController.showDebugInfo();
                        this.closeMenu();
                    }}>
                        <BugReportIcon />
                        <span>{t("Debug Info")}</span>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => {
                        trackActivity("HelpMenu", "KeyboardShortcuts", null, null, {}, { audit: false });
                        AppController.setExternalActivePanel(PanelType.SHORTCUTS);
                        this.closeMenu();
                    }}>
                        <span>{t("Keyboard Shortcuts")}</span>
                        <span style={{ marginLeft: 50 }}>⌘ ?</span>
                    </MenuItem>

                    {shiftIsPressed && <Divider />}
                    {shiftIsPressed &&
                        <Info>
                            <BuildVersion>{t("Build")} {buildVersion}</BuildVersion>
                        </Info>
                    }

                    {isInternalBAIUser(this.user.getEmail()) &&
                        <MenuItem onClick={() => {
                            // Hide all extenal panels, i.e. shortcut panel but keep the other internal panels on
                            // in order to allow user to see the bug report it better
                            AppController.setExternalActivePanel(null);
                            AppController.showInternalBugReport();
                            this.closeMenu();
                        }}>
                            <BugReportIcon />
                            <span>{t("Internal Bug Report")}</span>
                        </MenuItem>}
                </Menu>

                {showDivider && <>
                    <Gap20 />
                    <Divider vertical style={{
                        background: "#666"
                    }} />
                </>}

                <Gap20 />
                <UserMenuTrigger onClick={this.showMenu}>
                    {this.renderAvatar(workspace, { id: "userMenu", onClick: this.showMenu, style: { width: "24px", height: "24px" } })}
                    {showWorkspaceName && !app.isMobileOrTablet && (
                        <WorkspaceNameContainer>
                            <WorkspaceNameText>{workspace.name}</WorkspaceNameText>
                            {workspace.userRole === WorkspaceUserRole.GUEST && <>
                                <Gap15 />
                                <GuestBadge />
                                <Gap5 />
                            </>}
                            {anchorEl ? <ArrowDropUp /> : <ArrowDropDown />}
                        </WorkspaceNameContainer>
                    )}
                </UserMenuTrigger>
                {workspace && <StyledMenu
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    getContentAnchorEl={null}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right"
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    onClose={this.closeMenu}
                    autoFocus={false}
                    PaperProps={{
                        style: {
                            width: workspacesMenuWidth || "auto"
                        }
                    }}
                >
                    <MenuItem
                        onClick={this.handleEditProfile}
                        style={{
                            flexShrink: 0
                        }}>
                        <Icon style={{ marginRight: "0" }}>account_circle</Icon>
                        <Gap15 />
                        <span>{t("Manage Account")}</span>
                    </MenuItem>
                    <Divider style={{ marginBottom: "5px" }} />
                    {canSwitchWorkspace &&
                        <SearchContainer onKeyDown={this.handleSearchKeyDown}>
                            <WorkspaceTitle>
                                {t("Workspaces")}
                            </WorkspaceTitle>
                            <MenuSearchInput
                                showIcon={false}
                                focus
                                placeholder={t("Search workspaces...")}
                                onChange={query => this.setState({ workspaceSearchQuery: query || "" })}
                                handleClearSearch={() => this.setState({ workspaceSearchQuery: "" })}
                            />
                        </SearchContainer>
                    }
                    <ScrollableMenuContent id="user-menu-workspace-list" onKeyDownCapture={this.handleWorkspaceListKeyDownCapture}>
                        {this.renderSwitchWorkspaceMenu()}
                    </ScrollableMenuContent>
                    <StickyMenuFooter>
                        {!app.integrator && !window.isZoomApp &&
                            <MenuItem key="create" onClick={this.handleCreateWorkspace}>
                                <AddIcon style={{ marginRight: "0" }} />
                                <Gap15 />
                                <span>{t("New Workspace...")}</span>
                            </MenuItem>
                        }
                        <hr />
                        <MenuItem id="menu-item-logout" onClick={this.handleLogout}>
                            <Icon style={{ marginRight: "0" }}>logout</Icon>
                            <Gap15 />
                            <span>{t("Log out")}</span>
                        </MenuItem>
                    </StickyMenuFooter>
                </StyledMenu>}
            </Container>
        );
    }
}

export const UserMenu = withTranslation()(AppController.withState(UserMenuBase));
